import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';

/** Default when event.timezone is unset — matches server-side COALESCE in reminders/orientation SQL. */
export const DEFAULT_EVENT_TIMEZONE = 'America/New_York';

export function resolveEventTimeZone(event) {
  const tz = event?.timezone;
  if (typeof tz === 'string' && tz.trim()) return tz.trim();
  return DEFAULT_EVENT_TIMEZONE;
}

/** Short label for session editor hint (not exhaustive). */
export function shortTimezoneLabel(iana) {
  if (iana === 'America/New_York') return 'Eastern Time (US)';
  if (iana === 'America/Chicago') return 'Central Time (US)';
  if (iana === 'America/Denver') return 'Mountain Time (US)';
  if (iana === 'America/Los_Angeles') return 'Pacific Time (US)';
  return iana;
}

/**
 * datetime-local value (wall clock in `timeZone`) → ISO UTC for timestamptz.
 */
export function sessionLocalToIso(localDatetime, timeZone = DEFAULT_EVENT_TIMEZONE) {
  if (!localDatetime) return null;
  const s = localDatetime.length === 16 ? `${localDatetime}:00` : localDatetime;
  return fromZonedTime(s, timeZone).toISOString();
}

/**
 * DB ISO instant → datetime-local string in `timeZone`.
 */
export function isoToSessionLocal(iso, timeZone = DEFAULT_EVENT_TIMEZONE) {
  if (!iso) return '';
  const z = toZonedTime(iso, timeZone);
  return format(z, "yyyy-MM-dd'T'HH:mm");
}

export function formatSessionDateInTimeZone(isoString, timeZone = DEFAULT_EVENT_TIMEZONE) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
}

/** e.g. "2:00 PM EDT" */
export function formatSessionClockWithZone(isoString, timeZone = DEFAULT_EVENT_TIMEZONE) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  }).format(d);
}

/** Single-line date + time + zone for compact UI. */
export function formatSessionDateTimeWithZone(isoString, timeZone = DEFAULT_EVENT_TIMEZONE) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  }).format(d);
}

/**
 * Sort sessions for display (earliest first). Sessions without `start_time` sort last; ties use `id`.
 * @param {Array<Record<string, unknown>> | null | undefined} sessions
 * @returns {Array<Record<string, unknown>>}
 */
export function sortEventSessionsByStartTime(sessions) {
  if (!Array.isArray(sessions) || sessions.length === 0) {
    return Array.isArray(sessions) ? [...sessions] : [];
  }
  return [...sessions].sort((a, b) => {
    const ta = a?.start_time ? new Date(a.start_time).getTime() : NaN;
    const tb = b?.start_time ? new Date(b.start_time).getTime() : NaN;
    const aHas = Number.isFinite(ta);
    const bHas = Number.isFinite(tb);
    if (!aHas && !bHas) return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
    if (!aHas) return 1;
    if (!bHas) return -1;
    if (ta !== tb) return ta - tb;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  });
}
